import { useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  FormLabel,
  Modal,
  ModalDialog,
  Option,
  Select,
  Stack,
  Typography,
} from "@mui/joy";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiClient } from "../../lib/api";
import type { Task } from "../../types/tasks";
import { useOrganizationUsers } from "../../hooks/useOrganizationUsers";
import { TaskScheduleFields } from "./TaskScheduleFields";

export function TaskBulkActions({
  tasks,
  selected,
  setSelected,
}: {
  tasks: Task[];
  selected: number[];
  setSelected: (ids: number[]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [action, setAction] = useState("status");
  const [changes, setChanges] = useState<Record<string, any>>({
    status: "completed",
  });
  const { users } = useOrganizationUsers();
  const cache = useQueryClient();
  const mutation = useMutation({
    mutationFn: async () =>
      (
        await apiClient.post<{
          results: { id: number; success: boolean; error?: unknown }[];
        }>("/api/tasks/bulk-update/", { ids: selected, changes })
      ).data,
    onSuccess: (data) => {
      cache.invalidateQueries({ queryKey: ["tasks"] });
      cache.invalidateQueries({ queryKey: ["task"] });
      setSelected(
        data.results.filter((row) => !row.success).map((row) => row.id),
      );
    },
  });
  return (
    <Box>
      <Typography level="body-sm">
        {selected.length} selected (up to 100)
      </Typography>
      <Button
        disabled={!selected.length}
        onClick={() => {
          mutation.reset();
          setOpen(true);
        }}
      >
        Update selected tasks
      </Button>
      <Modal open={open} onClose={() => !mutation.isPending && setOpen(false)}>
        <ModalDialog sx={{ width: "90vw", maxWidth: 620, overflow: "auto" }}>
          <Stack spacing={2}>
            <Typography level="h4">Review bulk update</Typography>
            <Typography>{selected.length} selected tasks</Typography>
            <Box sx={{ maxHeight: 180, overflow: "auto" }}>
              {selected.map((id) => (
                <Typography key={id} level="body-sm">
                  {tasks.find((task) => task.id === id)?.name || `Task ${id}`}
                </Typography>
              ))}
            </Box>
            <FormControl>
              <FormLabel>Change</FormLabel>
              <Select
                value={action}
                onChange={(_, value) => {
                  const next = value!;
                  setAction(next);
                  setChanges(
                    next === "status"
                      ? { status: "completed" }
                      : next === "priority"
                        ? { priority: "medium" }
                        : next === "assigned_to"
                          ? { assigned_to: null }
                          : { due_date: null, is_all_day: true },
                  );
                  mutation.reset();
                }}
              >
                <Option value="status">Status</Option>
                <Option value="assigned_to">Assignee</Option>
                <Option value="due_date">Due date</Option>
                <Option value="priority">Priority</Option>
              </Select>
            </FormControl>
            {action === "due_date" ? (
              <TaskScheduleFields
                due={changes.due_date}
                allDay={changes.is_all_day}
                onChange={(due, allDay) =>
                  setChanges({ due_date: due, is_all_day: allDay })
                }
              />
            ) : (
              <Select
                value={changes[action] ?? ""}
                onChange={(_, value) =>
                  setChanges({
                    [action]:
                      action === "assigned_to"
                        ? value === ""
                          ? null
                          : Number(value)
                        : value,
                  })
                }
              >
                {action === "assigned_to"
                  ? [
                      <Option key="none" value="">
                        Unassigned
                      </Option>,
                      ...users.map((user) => (
                        <Option key={user.id} value={user.id}>
                          {user.first_name} {user.last_name}
                        </Option>
                      )),
                    ]
                  : (action === "status"
                      ? [
                          "not_started",
                          "in_progress",
                          "on_hold",
                          "completed",
                          "cancelled",
                        ]
                      : ["low", "medium", "high", "critical"]
                    ).map((value) => (
                      <Option key={value} value={value}>
                        {value.replaceAll("_", " ")}
                      </Option>
                    ))}
              </Select>
            )}
            {mutation.isError && (
              <Alert color="danger">
                The request failed; no successful result was confirmed. Retry
                after checking your connection.
              </Alert>
            )}
            {mutation.data?.results.map((row) => (
              <Alert key={row.id} color={row.success ? "success" : "danger"}>
                Task {row.id}:{" "}
                {row.success ? "Updated" : JSON.stringify(row.error)}
              </Alert>
            ))}
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                loading={mutation.isPending}
                disabled={!selected.length}
                onClick={() => mutation.mutate()}
              >
                Apply to selected tasks
              </Button>
              <Button
                variant="plain"
                disabled={mutation.isPending}
                onClick={() => setOpen(false)}
              >
                Close
              </Button>
            </Box>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
